'use strict'

import Splide from '@splidejs/splide'

export const timeline = (() => {
  // ── New style: tree-background trunk tiling ──────────────────────
  const CANOPY_H = 983
  const ROOTS_H = 693
  const TRUNK_TILE_H = 377
  const TILE_STEP = TRUNK_TILE_H - 1

  const renderTrunkTiles = (trunk, count) => {
    trunk.replaceChildren()
    for (let i = 0; i < count; i++) {
      trunk.appendChild(document.createElement('div'))
    }
  }

  // The canopy pulls the trunk up 1px (margin-bottom: -1px in _timeline.css), so
  // it only consumes 982px of the flex column, not its full 983px height.
  const CANOPY_OCCUPIES = CANOPY_H - 1

  // The trunk is built from whole 377px tiles, so the space they fill has to be
  // padded out to land exactly on a tile boundary. Any remainder shows up as a
  // hairline of background between the last tile and the roots — the white line
  // that blinks in and out as you resize the viewport a pixel at a time.
  //
  // Two things are needed to make that remainder impossible, and missing either
  // one leaves it behind:
  //
  //   1. Measure the real, fractional height. `offsetHeight` rounds to a whole
  //      pixel, and the events column routinely lands on sub-pixel heights
  //      (image heights are width x ratio), so the padding gets computed against
  //      a height that isn't the actual one.
  //   2. Subtract what the canopy *occupies* (982), not its height (983). The
  //      1px difference is real space the tiles have to cover.
  //
  // Pad by the exact fractional deficit — deliberately not rounded. Rounding it
  // to a whole pixel puts the fraction straight back. Verified on staging: the
  // trunk box then measures exactly count * TILE_STEP + 1, and the roots sit 1px
  // over the last tile as intended instead of a fraction of a pixel short.
  const alignTrunk = (section, treeBg, trunk) => {
    section.style.paddingTop = ''
    trunk.replaceChildren()
    const basePad = parseFloat(getComputedStyle(section).paddingTop) || 0
    const available = treeBg.getBoundingClientRect().height - CANOPY_OCCUPIES - ROOTS_H
    if (available < TRUNK_TILE_H) return
    const count = Math.max(1, Math.ceil((available - 1) / TILE_STEP))
    const deficit = count * TILE_STEP + 1 - available
    if (deficit > 0.001) section.style.paddingTop = `${basePad + deficit}px`
    renderTrunkTiles(trunk, count)
  }

  const initTree = () => {
    const section = document.querySelector('.timeline-tree')
    const treeBg = section?.querySelector('.tree-bg')
    const trunk = section?.querySelector('.tree-bg__trunk')
    const events = section?.querySelector('.timeline-events')
    if (!treeBg || !trunk || !events) return
    const run = () => alignTrunk(section, treeBg, trunk)
    run()
    window.addEventListener('resize', run)
    new ResizeObserver(run).observe(events)
  }

  // ── Legacy style: Splide year carousel + scroll-synced stories ───
  let slider
  let yearButtons

  const handleYearButton = (button, scroll = true, slide = true) => {
    const year = button.dataset.year
    const yearStoriesBlock = document.querySelector(`#timeline-stories > [data-year="${year}"]`)
    const firstYearStory = yearStoriesBlock.querySelector('.story')

    if (scroll) {
      // Scroll to the first story of the year
      firstYearStory.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    if (slide) {
      // Slide to the year
      const slideIndex = Array.prototype.indexOf.call(yearButtons, button)
      if (slideIndex >= 0 && slideIndex !== -1) {
        slider.go(slideIndex)
      }
    }

    // Add active class to the button
    yearButtons.forEach(button => button.classList.remove('is-active'))
    button.classList.add('is-active')
  }

  const initTimeline = () => {
    const sliderContainer = document.getElementById('timeline')

    if (!sliderContainer) return

    slider = new Splide(sliderContainer)
    slider.mount()

    yearButtons = sliderContainer.querySelectorAll('button[data-year]')

    // Listen to a button click
    yearButtons.forEach((button, i) => {
      button.addEventListener('click', () => {
        handleYearButton(button)
      })
    })

    // Listen to the slider movements
    slider.on('moved', (newIndex) => {
      const realIndex = newIndex % yearButtons.length
      handleYearButton(yearButtons[realIndex], false, false)
    })
  }

  const initStories = () => {
    const storiesContainer = document.getElementById('timeline-stories')

    if (!storiesContainer) return

    const stories = storiesContainer.querySelectorAll('.story')

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active')

          const yearBlock = entry.target.parentElement
          const index = yearBlock?.dataset?.index

          if (index) {
            const realIndex = index % yearButtons.length
            handleYearButton(yearButtons[realIndex], false)
          }
        } else {
          entry.target.classList.remove('active')
        }
      })
    }, {
      root: null,
      threshold: 0.9,
    })

    stories.forEach((story) => observer.observe(story))
  }

  const init = () => {
    // Runs whichever markup the block rendered; each helper no-ops if absent.
    initTree()
    initTimeline()
    initStories()
  }

  return { init }
})()
